import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { isArray, isEmpty, omitBy, isNil } from "lodash";
import moment from "moment";
import { ReportOrderList } from "Components/order-management/report-order-list";
import ModuleTitleBar from "Components/shared/module-title-bar";
import { BasicModal, SelectInput } from "@life-line-lab/components-react";
import { Col, Switch, Tooltip } from "antd";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { toaster } from "Helpers/toaster";
import usePagination from "Hooks/pagination.hook";
import useQuery from "Hooks/query.hook";
import useSession from "Hooks/session.hook";
import {
  cancelRejectOrder,
  fetchOrderList,
  updateOrderStatus,
  updateReverseOrderStatus,
} from "Services/order.service";
import orderStatusesMap from "Data/order-statuses.json";
import { QuestionCircleOutlined } from "@ant-design/icons";
import "./all-orders.scss";
import { routeToOrderView } from "Helpers/route-order-view";
import { useHistory, useLocation } from "react-router-dom";
import FlagRejectParcelModal from "Components/order-management/shared/parcel-modals/flag-reject-parcel";

// This is for initial payload, and hide DELIVERED
const allOrderStatuses = Object.values(orderStatusesMap).map(
  (status) => status.COMPANY,
);

// This is for the reverse flow
const globalStatusOptions = {
  PICKUP_IN_PROGRESS: [
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_PICKUP.COMPANY),
      value: orderStatusesMap.PENDING_PICKUP.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.REJECTED.COMPANY),
      value: orderStatusesMap.REJECTED.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.CANCELLED.COMPANY),
      value: orderStatusesMap.CANCELLED.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_CONFIRMATION.COMPANY),
      value: orderStatusesMap.PENDING_CONFIRMATION.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_REVIEW.COMPANY),
      value: orderStatusesMap.PENDING_REVIEW.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.OPEN.COMPANY),
      value: orderStatusesMap.OPEN.COMPANY,
    },
  ],
  DROP_OFF_IN_PROGRESS: [
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_DROP_OFF.COMPANY),
      value: orderStatusesMap.PENDING_DROP_OFF.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.REJECTED.COMPANY),
      value: orderStatusesMap.REJECTED.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.CANCELLED.COMPANY),
      value: orderStatusesMap.CANCELLED.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_CONFIRMATION.COMPANY),
      value: orderStatusesMap.PENDING_CONFIRMATION.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_REVIEW.COMPANY),
      value: orderStatusesMap.PENDING_REVIEW.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.OPEN.COMPANY),
      value: orderStatusesMap.OPEN.COMPANY,
    },
  ],
  REJECTED: [
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_REVIEW.COMPANY),
      value: orderStatusesMap.PENDING_REVIEW.COMPANY,
    },
  ],
  CANCELLED: [
    {
      label: toCapitalisedSpaced(orderStatusesMap.PENDING_REVIEW.COMPANY),
      value: orderStatusesMap.PENDING_REVIEW.COMPANY,
    },
  ],
  PENDING_CONFIRMATION: [
    {
      label: toCapitalisedSpaced(orderStatusesMap.OPEN.COMPANY),
      value: orderStatusesMap.OPEN.COMPANY,
    },
  ],
  OTHERS: [
    {
      label: toCapitalisedSpaced(orderStatusesMap.CANCELLED.COMPANY),
      value: orderStatusesMap.CANCELLED.COMPANY,
    },
    {
      label: toCapitalisedSpaced(orderStatusesMap.REJECTED.COMPANY),
      value: orderStatusesMap.REJECTED.COMPANY,
    },
  ],
};

interface ModalResponseType {
  remarks?: string;
  issueType?: { id: string; name: string; remarks: string };
}

const useAllOrders = (props: any) => {
  const {
    filterObj,
    setReportListing,
    queryParams,
    itemPerPage,
    setCurrentListCount,
    t,
  } = props || {};
  const [isChildLoading, setIsChildLoading] = useState<boolean>(false);

  const handleFetchAllOrders = async () => {
    setIsChildLoading && setIsChildLoading(true);
    const responseData: any = await fetchOrderList(
      omitBy({ ...filterObj, hideDeliveredAfterTwoDay: false }, isNil),
      {
        offset: queryParams.offset,
        limit: itemPerPage,
      },
      { sortBy: "orderStatus", mode: "DESC" },
    );

    if (responseData && !responseData.error) {
      setReportListing && setReportListing(responseData);
      setCurrentListCount(responseData.length);
    } else {
      toaster("error", t("error.badRequest"));
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleStatusUpdate = async (reportID, newStatus) => {
    setIsChildLoading && setIsChildLoading(true);

    const responseData = await updateOrderStatus([reportID], newStatus, null);
    if (!responseData.error && responseData) {
      toaster("success", "Status updated successfully");
      handleFetchAllOrders && handleFetchAllOrders();
    } else {
      toaster("error", responseData.error.message);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleCancelReject = async (
    reportID: string,
    newStatus: string,
    modalResponse: ModalResponseType,
  ) => {
    setIsChildLoading && setIsChildLoading(true);

    const { issueType, remarks } = modalResponse || {};

    const responseData = await cancelRejectOrder(
      reportID,
      newStatus,
      remarks,
      issueType,
    );

    if (!responseData.error && responseData) {
      toaster("success", "Status updated successfully");
      handleFetchAllOrders && handleFetchAllOrders();
    } else {
      toaster("error", responseData.error.message);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleReverseStatusUpdate = async (reportID, newStatus) => {
    setIsChildLoading && setIsChildLoading(true);

    const responseData = await updateReverseOrderStatus(
      [reportID],
      newStatus,
      null,
    );
    if (!responseData.error && responseData) {
      toaster("success", "Status updated successfully");
      handleFetchAllOrders && handleFetchAllOrders();
    } else {
      toaster("error", responseData.error.message);
    }

    setIsChildLoading && setIsChildLoading(false);
  };

  const handleFlowUpdate = (reportID, newStatus, forwardFlow) => {
    if (forwardFlow) {
      handleStatusUpdate(reportID, newStatus);
    } else {
      handleReverseStatusUpdate(reportID, newStatus);
    }
  };

  return {
    isChildLoading,
    handleCancelReject,
    handleFetchAllOrders,
    handleFlowUpdate,
  };
};

const AdditionalComponent = (props: any) => {
  const { handleCompletedShown, isCompletedHidden } = props || {};
  const { t } = useTranslation();

  const HideCompletedSelect = () => {
    return (
      <Col span={12} xl={6}>
        <div className="mb-7 rf-field">
          {t("label.hideCompleted")}
          <Tooltip
            title="This will only work if status is filtered to All."
            placement="top"
          >
            <QuestionCircleOutlined />
          </Tooltip>
        </div>
        <Switch
          defaultChecked
          checked={isCompletedHidden}
          onChange={(value) => {
            handleCompletedShown(value);
          }}
        />
      </Col>
    );
  };

  return <HideCompletedSelect />;
};

const AllOrders = () => {
  const { t } = useTranslation();
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const { org } = useSession();

  const initialFilters = {
    orgID: [org._id],
    orderStatus: allOrderStatuses,
  };

  // dont set initialFilters into filterObj like from other components, because that will cause two same API calls
  const [reportListing, setReportListing] = useState<any>([]);
  const [filterObj, setFilterObj] = useState<any>({});
  const [isCompletedHidden, setIsCompletedHidden] = useState<boolean>(false);
  const page: any = query.get("page") || 1;
  const [currentListCount, setCurrentListCount] = useState(0);
  const initialModal: any = {
    open: false,
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);
  const { paginationComponent, itemPerPage, queryParams } = usePagination({
    currentListCount,
    currentPath: "/order-management/all",
    resetPageDeps: filterObj
  });

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      setFilterObj(initialFilters);
      return;
    }

    const orderStatus = () => {
      const final = filterInput?.status
        ? isArray(filterInput?.status)
          ? filterInput?.status
          : [filterInput?.status]
        : allOrderStatuses;

      return isCompletedHidden
        ? final.filter((status: string) => status !== "DELIVERED")
        : final;
    };

    const filterValue = {
      ...filterObj,
      orgID: [org._id],
      fromDate: filterInput?.dateFrom || null,
      toDate: filterInput?.dateTo || null,
      orderStatus: orderStatus(),
      orFields: filterInput?.orFields || null,
    };

    setFilterObj && setFilterObj(filterValue);
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const {
    handleCancelReject,
    handleFetchAllOrders,
    handleFlowUpdate,
    isChildLoading,
  } = useAllOrders({
    setReportListing,
    filterObj,
    queryParams,
    itemPerPage,
    setCurrentListCount,
    t,
  });

  useEffect(() => {
    if (!isEmpty(filterObj)) handleFetchAllOrders && handleFetchAllOrders();
  }, [queryParams.offset, itemPerPage, filterObj]);

  const handleCompletedShown = (value) => {
    setIsCompletedHidden && setIsCompletedHidden(value);

    // needed as users can filter by status (higher priority) or toggle by button
    if (filterObj.orderStatus.length > 1) {
      // if toggle is false (show DELIVERED) and there is no DELIVERED, put the value in
      if (!value && !filterObj.orderStatus.includes("DELIVERED")) {
        onFilter({ status: filterObj.orderStatus.concat(["DELIVERED"]) });
      } else if (value && filterObj.orderStatus.includes("DELIVERED")) {
        // if toggle is true (hide DELIVERED) and there is a DELIVERED, remove it
        onFilter({
          status: filterObj.orderStatus.filter(
            (status) => status != "DELIVERED",
          ),
        });
      }
    }
  };

  const handleModalAction = (
    action: string,
    orderID: string,
    senderInfo: any,
  ) => {
    setCallModal(
      <FlagRejectParcelModal
        action={action === "REJECTED" ? "REJECT" : "CANCEL"}
        orderID={orderID}
        customer={senderInfo}
        onSubmit={(modalResponse: ModalResponseType) => {
          handleCancelReject(orderID, action, modalResponse);
        }}
        onCancel={handleResetModalState}
      />,
    );

    setModal({ open: true });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "no",
      render: (text: any, record: any, index: number) =>
        (page - 1) * itemPerPage + (index + 1),
    },
    {
      title: t("label.orderCode").toUpperCase(),
      dataIndex: ["orderCode"],
      ellipsis: true,
      render: (orderCode: string, record: any) => {
        return (
          <a
            className="clickable-id"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              routeToOrderView(record._id, location, history);
            }}
          >
            {orderCode || "-"}
          </a>
        );
      },
    },
    {
      title: t("label.batchNumber").toUpperCase(),
      dataIndex: ["midMileInfo", "batchCode"],
      ellipsis: true,
    },
    {
      title: t("label.trackingNo").toUpperCase(),
      dataIndex: ["trackingNumber"],
      ellipsis: true,
    },
    {
      title: t("label.customer").toUpperCase(),
      dataIndex: ["customer", "contact", "fullName"],
      ellipsis: true,
    },
    {
      title: t("label.orderDateAndTime").toUpperCase(),
      dataIndex: ["createdAt"],
      ellipsis: true,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
    {
      title: t("label.totalWeight").toUpperCase(),
      dataIndex: ["weight"],
      ellipsis: true,
    },
    {
      title: t("label.totalVolume").toUpperCase(),
      dataIndex: ["volume"],
      ellipsis: true,
    },
    {
      title: t("label.deliveryDate").toUpperCase(),
      dataIndex: ["deliveryDate"],
      ellipsis: true,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
    {
      title: t("label.pickUpDateAndTime").toUpperCase(),
      dataIndex: ["firstMileInfoData", "pickupData", "pickupDate"],
      key: "pickupDate",
      ellipsis: true,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
    {
      title: t("label.deliveryType").toUpperCase(),
      dataIndex: ["additionalCharges"],
      ellipsis: true,
      render: (arr: any) =>
        !isEmpty(arr)
          ? arr?.map((charge) => charge.type == "DELIVERY_TYPE" && charge.name)
          : null,
    },
    {
      title: t("label.serviceType").toUpperCase(),
      dataIndex: ["additionalCharges"],
      ellipsis: true,
      render: (arr: any) =>
        !isEmpty(arr)
          ? arr?.map((charge) => charge.type == "SERVICE_TYPE" && charge.name)
          : null,
    },
    {
      title: t("label.journeyCode").toUpperCase(),
      dataIndex: ["midMileInfoData", "trainPlan", "journeyCode"],
      ellipsis: true,
    },
    {
      title: t("label.trolleyID").toUpperCase(),
      dataIndex: ["midMileInfoData", "trolley", "code"],
      ellipsis: true,
    },
    {
      title: t("label.boxID").toUpperCase(),
      dataIndex: ["midMileInfoData", "box", "code"],
      ellipsis: true,
    },
    {
      title: t("label.loadTime").toUpperCase(),
      dataIndex: ["midMileInfo", "loadDateTime"],
      ellipsis: true,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
    {
      title: t("label.loadAt").toUpperCase(),
      dataIndex: ["midMileInfoData", "loadAt", "name"],
      ellipsis: true,
    },
    {
      title: t("label.unloadTime").toUpperCase(),
      dataIndex: ["midMileInfo", "unloadDateTime"],
      ellipsis: true,
      render: (text: any) =>
        text ? moment(text).format("DD-MM-YYYY hh:mm a") : null,
    },
    {
      title: t("label.unloadAt").toUpperCase(),
      dataIndex: ["midMileInfoData", "unloadAt", "name"],
      ellipsis: true,
    },
    {
      title: t("label.lastKnownLocation").toUpperCase(),
      dataIndex: ["midMileInfoData", "lastKnownLocation", "name"],
      ellipsis: true,
    },
    {
      title: t("label.originHub").toUpperCase(),
      dataIndex: ["midMileInfoData", "sortingHub", "name"],
      ellipsis: true,
    },
    {
      title: t("label.handoverPoint").toUpperCase(),
      dataIndex: ["midMileInfoData", "handoverPoint", "name"],
      ellipsis: true,
    },
    {
      title: t("label.status").toUpperCase(),
      dataIndex: ["orderStatusInfo", "orderStatus"],
      // do not put ellipsis to prevent select dropdown from rendering inside the cell
      render: (text: string, record: any) => {
        // reverse process
        // =======================================
        // 1. Pickup in Progress -> Pending Pickup, Rejected, Cancelled, P.Confirm, P.Review, Open
        // 2. Drop off in Progress -> Pending Dropoff, Rejected, Cancelled, P.Confirm, P.Review, Open
        // 3. Rejected -> Pending Review
        // 4. Cancelled -> Pending Review
        // 5. P.Confirm -> Open

        // forward process
        // =======================================
        // 6. Delivery in Progress -> Delivered (for THIRD_PARTY only)

        // cancel reject process
        // =======================================
        // 7. All other status -> Rejected / Cancelled (for SUPER_ADMIN only)

        const isThirdParty =
          record?.assignedDriver === "THIRD_PARTY" ? true : false;
        const status = text;

        let statusOptions: any = [];
        let forwardFlow = false;

        if (isThirdParty) {
          // enable forward process
          if (status === orderStatusesMap.DELIVERY_IN_PROGRESS.COMPANY) {
            statusOptions.push({
              label: toCapitalisedSpaced(orderStatusesMap.DELIVERED.COMPANY),
              value: orderStatusesMap.DELIVERED.COMPANY,
            });
            forwardFlow = true;
          }
        } else {
          // if return null, the status has no reverse flow
          statusOptions =
            globalStatusOptions?.[status] ?? globalStatusOptions?.OTHERS;
        }

        // disable the input if there are no options
        const isDisabled = isEmpty(statusOptions) ? true : false;

        return (
          <div className={reportListing?.length < 2 ? "ao-input" : ""}>
            <SelectInput
              value={text ? toCapitalisedSpaced(text) : ""}
              isReadonly={isDisabled}
              options={statusOptions}
              onChange={(value) => {
                if (statusOptions !== globalStatusOptions.OTHERS)
                  handleFlowUpdate(record._id, value, forwardFlow);
                else
                  handleModalAction(
                    value as string,
                    record._id,
                    record.senderInfo,
                  );
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="report-orders all-orders">
      <Helmet>
        <title>{t("pageTitle.allOrders")}</title>
      </Helmet>
      <ModuleTitleBar
        titleText={t("title.allOrders")}
        haveSubtitle
        subtitleText={t("title.reporting")}
      />
      <ReportOrderList
        columns={columns}
        onFilter={onFilter}
        reportListing={reportListing}
        enableDownload={false}
        onDownloadExcelFile={null}
        enableACF={false}
        disableShowLatestStatus={true}
        handleRefreshList={handleFetchAllOrders}
        isLoading={isChildLoading}
        additionalComponent={
          <AdditionalComponent
            isCompletedHidden={isCompletedHidden}
            handleCompletedShown={handleCompletedShown}
          />
        }
      />
      <div className="my-30">{paginationComponent}</div>
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={true}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </div>
  );
};

export default AllOrders;
